<script lang="ts">
  /**
   * Mail administration (Settings → Mail administration), shown only to the mailbox listed
   * as an admin of this site in tenants.json. The server decides that; this component only
   * renders what `GET /api/admin/whoami` reports, and every route it calls is enforced again
   * on the server and once more inside the privileged helper.
   *
   * Passwords set here change the real mail account, so the reset form says so out loud.
   */
  import { ApiError, api } from '$lib/api/client';
  import { t } from '$lib/i18n';
  import { formatQuota } from '$lib/mail/format';
  import type { AdminMailbox } from '$lib/api/types';

  const USER_RE = /^[a-z0-9]([a-z0-9._-]{0,30}[a-z0-9])?$/;

  let available = $state(false);
  let domain = $state('');
  let boxes = $state<AdminMailbox[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let note = $state<string | null>(null);

  // create form
  let adding = $state(false);
  let newUser = $state('');
  let newReal = $state('');
  let newPass = $state('');
  let newQuota = $state('1024');

  // per-row forms
  let resetting = $state<string | null>(null);
  let resetPass = $state('');
  let quotaFor = $state<string | null>(null);
  let quotaMb = $state('');

  let busy = $state(false);

  $effect(() => {
    void boot();
  });

  async function boot(): Promise<void> {
    try {
      const who = await api.adminWhoami();
      available = who.admin && who.available;
      domain = who.domains[0] ?? '';
      if (available) await refresh();
    } catch {
      available = false;
    }
  }

  async function refresh(): Promise<void> {
    loading = true;
    error = null;
    try {
      const r = await api.adminMailboxes(domain || undefined);
      boxes = r.mailboxes;
      domain = r.domain;
    } catch (e) {
      error = t(e instanceof ApiError && e.code === 'admin_unavailable' ? 'adminUnavailable' : 'adminFailed');
    } finally {
      loading = false;
    }
  }

  function failure(e: unknown): string {
    if (e instanceof ApiError) {
      if (e.code === 'already_exists') return t('adminExists');
      if (e.code === 'bad_user') return t('adminBadUser');
      if (e.code === 'password_too_short' || e.code === 'password_contains_user') return t('adminPasswordShort');
      if (e.code === 'admin_unavailable') return t('adminUnavailable');
    }
    return t('adminFailed');
  }

  /** A readable, high-entropy suggestion so an admin never invents a weak one. */
  function generate(): string {
    const alphabet = 'abcdefghijkmnpqrstuvwxyz23456789';
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
      out += alphabet[bytes[i]! % alphabet.length];
      if (i % 4 === 3 && i < bytes.length - 1) out += '-';
    }
    return out;
  }

  async function create(): Promise<void> {
    const user = newUser.trim().toLowerCase();
    if (!USER_RE.test(user)) { error = t('adminBadUser'); return; }
    if (newPass.length < 10) { error = t('adminPasswordShort'); return; }
    busy = true;
    error = null;
    note = null;
    try {
      const r = await api.adminCreateMailbox({
        user, password: newPass, realName: newReal.trim() || undefined,
        quotaMb: Number(newQuota) || 0, domain,
      });
      note = t('adminCreated', { email: r.mailbox?.email ?? `${user}@${domain}` });
      adding = false;
      newUser = '';
      newReal = '';
      newPass = '';
      await refresh();
    } catch (e) {
      error = failure(e);
    } finally {
      busy = false;
    }
  }

  async function resetPassword(box: AdminMailbox): Promise<void> {
    if (resetPass.length < 10) { error = t('adminPasswordShort'); return; }
    busy = true;
    error = null;
    note = null;
    try {
      await api.adminSetPassword({ user: box.user, password: resetPass, domain });
      note = t('adminResetDone', { email: box.email });
      resetting = null;
      resetPass = '';
    } catch (e) {
      error = failure(e);
    } finally {
      busy = false;
    }
  }

  async function saveQuota(box: AdminMailbox): Promise<void> {
    busy = true;
    error = null;
    note = null;
    try {
      await api.adminSetQuota({ user: box.user, quotaMb: Number(quotaMb) || 0, domain });
      note = t('adminQuotaDone', { email: box.email });
      quotaFor = null;
      await refresh();
    } catch (e) {
      error = failure(e);
    } finally {
      busy = false;
    }
  }

  function openQuota(box: AdminMailbox): void {
    quotaFor = box.user;
    quotaMb = box.quotaBytes ? String(Math.round(box.quotaBytes / 1024 / 1024)) : '0';
    resetting = null;
  }

  function openReset(box: AdminMailbox): void {
    resetting = box.user;
    resetPass = generate();
    quotaFor = null;
  }
</script>

{#if available}
  <section class="admin">
    <h3>{t('adminSection')}</h3>
    <div class="card">
      <p class="muted">{t('adminIntro', { domain })}</p>
      {#if note}<p class="ok" role="status">{note}</p>{/if}
      {#if error}<p class="err" role="alert">{error}</p>{/if}

      <ul class="boxes">
        {#each boxes as box (box.user)}
          <li>
            <div class="head">
              <span class="addr" dir="ltr">{box.email}</span>
              <span class="use">
                {formatQuota(box.usedBytes)}
                {#if box.quotaBytes}/ {formatQuota(box.quotaBytes)}{:else}/ {t('adminNoLimit')}{/if}
              </span>
            </div>
            <div class="actions">
              <button type="button" onclick={() => openReset(box)}>{t('adminReset')}</button>
              <button type="button" onclick={() => openQuota(box)}>{t('adminQuotaSet')}</button>
            </div>

            {#if resetting === box.user}
              <div class="form">
                <p class="warn">{t('adminResetWarn')}</p>
                <label>
                  <span>{t('adminResetFor', { email: box.email })}</span>
                  <input type="text" dir="ltr" spellcheck="false" bind:value={resetPass} />
                </label>
                <div class="actions">
                  <button type="button" onclick={() => { resetPass = generate(); }}>{t('adminGenerate')}</button>
                  <button type="button" onclick={() => { resetting = null; }}>{t('adminCancel')}</button>
                  <button type="button" class="primary" disabled={busy} onclick={() => resetPassword(box)}>{t('adminSave')}</button>
                </div>
              </div>
            {/if}

            {#if quotaFor === box.user}
              <div class="form">
                <label>
                  <span>{t('adminQuotaMb')}</span>
                  <input type="number" dir="ltr" min="0" step="64" bind:value={quotaMb} />
                </label>
                <div class="actions">
                  <button type="button" onclick={() => { quotaFor = null; }}>{t('adminCancel')}</button>
                  <button type="button" class="primary" disabled={busy} onclick={() => saveQuota(box)}>{t('adminSave')}</button>
                </div>
              </div>
            {/if}
          </li>
        {/each}
        {#if !boxes.length && !loading}
          <li class="muted empty">{t('adminMailboxes')}: 0</li>
        {/if}
      </ul>

      {#if adding}
        <div class="form">
          <label>
            <span>{t('adminUser')}</span>
            <input type="text" dir="ltr" spellcheck="false" bind:value={newUser} placeholder="sales" />
          </label>
          <label>
            <span>{t('adminRealName')}</span>
            <input type="text" bind:value={newReal} />
          </label>
          <label>
            <span>{t('adminPassword')}</span>
            <input type="text" dir="ltr" spellcheck="false" bind:value={newPass} />
          </label>
          <label>
            <span>{t('adminQuotaMb')}</span>
            <input type="number" dir="ltr" min="0" step="64" bind:value={newQuota} />
          </label>
          <div class="actions">
            <button type="button" onclick={() => { newPass = generate(); }}>{t('adminGenerate')}</button>
            <button type="button" onclick={() => { adding = false; }}>{t('adminCancel')}</button>
            <button type="button" class="primary" disabled={busy} onclick={create}>{t('adminCreate')}</button>
          </div>
        </div>
      {:else}
        <div class="actions">
          <button type="button" class="primary" onclick={() => { adding = true; newPass = generate(); error = null; }}>{t('adminAdd')}</button>
        </div>
      {/if}
    </div>
  </section>
{/if}

<style>
  /* @css settings/AdminMailboxes start */
  .card {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .muted {
    margin: 0;
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.7;
  }
  .boxes {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .boxes > li {
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
  }
  .addr {
    font-weight: 600;
  }
  .use {
    font-size: 12px;
    color: var(--text-muted);
    direction: ltr;
  }
  .actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-top: 1px dashed var(--border);
    padding-top: 8px;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
  }
  input {
    padding: 8px 10px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface);
    color: var(--text);
    font: inherit;
  }
  .warn {
    margin: 0;
    font-size: 12px;
    color: var(--warn-fg, #8a5a00);
    background: var(--warn-bg, #fff6e5);
    padding: 6px 8px;
    border-radius: 6px;
  }
  .err {
    margin: 0;
    color: var(--danger, #c0392b);
    font-size: 13px;
  }
  .ok {
    margin: 0;
    color: var(--ok-fg, #0a7a35);
    font-size: 13px;
  }
  .empty {
    border: 0;
    padding: 0;
  }
  /* @css settings/AdminMailboxes end */
</style>
